import dayjs from "dayjs";
import { ConditionType, ConditionState, type IVehicle, ConfigurationType } from "..";
import { OdometerPosition } from "./OdometerPosition";
import { ConditionLabels } from "./ConditionLabels";
import { DatePosition } from "./DatePosition";
import type { ICondition, IConditionDto } from "./ICondition";
import { IWorkOrderConfigurationDto } from "@domain/CarePlan/WorkOrders/IWorkOrderConfiguration";

export abstract class ConditionBase extends ConditionLabels implements ICondition
{


    $updateKey = 0;

    conditionId: string = '';
    consumerVehicleId: string = '';
    icon: string = 'dashboard:service';
    state: ConditionState = ConditionState.Incomplete;
    type: ConditionType = ConditionType.Service;
    value?: string;
    date: DatePosition = {} as DatePosition;
    odometer: OdometerPosition = {} as OdometerPosition;
    averageOdometerInterval: number = 0;
    dateDismissedByConsumer?: string;
    dismissedByUserId?: string;
    dateDismissedByUser?: string;
    averagePrice?: number;
    
    score: number = 1;
    isDue: boolean = false;
    isOverdue: boolean = false;
    isCritical: boolean = false;
    isUnconfigured: boolean = true;
    isVisible: boolean;
    hasAlert: boolean = false;
    hasLast: boolean = false;
    hasNext: boolean = false;
    hasPosition: boolean = false;
    hasData: boolean = false;
    canBeDisabled: boolean = false; 
    isDisabled: boolean = false;
    configurationType = ConfigurationType.Date;
    workOrderConfiguration?: IWorkOrderConfigurationDto;
    ordinal: number = 0;

    protected vehicle: IVehicle = {} as IVehicle;

    constructor( vehicle: IVehicle, model: IConditionDto, type: ConditionType, configurationType: ConfigurationType, usesOdometer: boolean = true )
    {
        
        super();

        this.vehicle = vehicle;
        this.conditionId = model.conditionId;
        this.consumerVehicleId = model.consumerVehicleId;
        this.type = type;
        this.state = model.state ?? ConditionState.Incomplete;
        this.value = model.value;
        this.configurationType = configurationType;
        this.averageOdometerInterval = model.averageOdometerInterval;
        this.odometer = new OdometerPosition( vehicle, model, vehicle.odometer, usesOdometer );
        this.date = new DatePosition( model, configurationType == ConfigurationType.Date );
        this.averagePrice = model.averagePrice;
        this.dateDismissedByConsumer = model.dateDismissedByConsumer;
        this.dismissedByUserId = model.dismissedByUserId;
        this.dateDismissedByUser = model.dateDismissedByUser;
        this.hasData = true;
        this.canBeDisabled = model.canBeDisabled;
        this.isDisabled = model.isDisabled;
        this.isVisible = true;

        // Handle scenarios where there is no date on a condition that requires a specific date
        // We may have a valid odometer, but the condition needs a specific date, so we have to
        // set it to incomplete.  This shouldnt happen in real life, but this guards against
        // corrupted data.
        if( this.configurationType == ConfigurationType.Date && this.date.isIncomplete )
        {
            this.state = ConditionState.Incomplete;    
        }

    }

    /**
     * Initialises the state after the key parameters have been set or updated
     */
    protected initialise()
    {

        // If the date is not set, configure it from the odometer so it has a value
        if( this.odometer.next > 0 && this.date.next.isValid() == false )
        {
            this.date.setFromOdometer( this, this.vehicle, this.odometer );
        }

        this.score = Math.min( this.date.score, this.odometer.score );
        this.hasLast = this.odometer.last > 0 || this.date.last.isValid();
        this.hasNext = this.odometer.next > 0 || this.date.next.isValid();

        // Set the state flags
        this.setFlags();

        // Labels have to be set after the flags/state
        this.updateLabels( this.vehicle, this );
        this.$updateKey++;

    }

    /**
     * Reinitialises the condition with data from the dto model
     * @param model 
     */
    reinitialise( model: IConditionDto )
    {
        this.state = model.state ?? ConditionState.Incomplete;
        this.averageOdometerInterval = model.averageOdometerInterval;
        this.odometer.set( model.nextOdometer ?? 0, model.currentOdometer, model.lastOdometer );
        this.date.set( dayjs( model.nextDate ), dayjs( model.lastDate ) );
        this.averagePrice = model.averagePrice;
        this.dateDismissedByConsumer = model.dateDismissedByConsumer;
        this.dismissedByUserId = model.dismissedByUserId;
        this.dateDismissedByUser = model.dateDismissedByUser;
        this.canBeDisabled = model.canBeDisabled;
        this.isDisabled = model.isDisabled;
        this.initialise();
    }

    /**
     * Date label is the primary display, but it needs to to be calculated because the odometer may be in a worse
     * state than the odometer
     * @param condition 
     * @returns 
     */
    getDateRelativeLabel(): string
    {
        if( this.odometer.isOverdue && this.date.isOverdue == false )
        {
            return this.labels.intervalState;    
        }

        else if( this.odometer.isDue && this.date.isDue == false && this.date.isOverdue == false )
        {
            return this.labels.intervalState;    
        }
        return this.date.relativeLabel
    }

    /**
     * Sets the state of the condition based on its attributes
     * @returns 
     */
    private setFlags()
    {

        // Critical states always take priority
        if( this.state == ConditionState.Critical )
        {
            this.isCritical = true;
            this.isDue = false;
            this.isOverdue = false;
            this.isUnconfigured = false;
            this.score = 0;
        }

        // If we're overdue on the odometer or date, set the state to overdue
        else if( this.state == ConditionState.Overdue )
        {
            this.isCritical = false;
            this.isDue = false;
            this.isOverdue = true;
            this.isUnconfigured = false;
        }

        // If we're due on the odometer or date, set the state to due
        else if( this.state == ConditionState.Due )
        {
            this.isDue = true;
            this.isCritical = false;
            this.isOverdue = false;
            this.isUnconfigured = false;
        }

        // If we're incomplete on all data set it to Unknown
        else if( this.state == ConditionState.Incomplete )
        {
            this.isDue = false;
            this.isCritical = false;
            this.isOverdue = false;
            this.isUnconfigured = true;
        }

        // Otherwise its healthy
        else
        {
            this.isDue = false;
            this.isCritical = false;
            this.isOverdue = false;
            this.isUnconfigured = false;
        }

        this.hasAlert = this.isCritical || this.isOverdue || this.isDue;
    }

    get isEnabled(): boolean
    {
        return !this.isDisabled;
    }

    set isEnabled( value: boolean )
    {
        this.isDisabled = !value;
    }

}